import { Collapse, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ReactNode, useState, useRef, useEffect } from "react";
import styles from "./style.module.scss";
import VLProgressBar, {
  VLProgressBarProps,
} from "../../ProgressBars/VLProgressBar";
import ChevronIcon from "../../../../assets/icons/ChevronIcon";

export interface CollapseCardItem {
  key: string;
  header: {
    label: string;
    tooltip?: string;
    progress?: VLProgressBarProps;
  };
  textButton?: string;
  children: ReactNode;
  noCollapse?: boolean;
}

interface VLCollapseCardProps {
  items: CollapseCardItem[];
  onClose?: (key: string) => void;
  defaultOpenKeys?: string[];
}

export default function VLCollapseCard({
  items,
  onClose,
  defaultOpenKeys,
}: VLCollapseCardProps) {
  const [activeKeys, setActiveKeys] = useState<string[]>(defaultOpenKeys || []);

  useEffect(() => {
    if (defaultOpenKeys) {
      setActiveKeys(defaultOpenKeys);
    }
  }, [defaultOpenKeys]);

  const closedKeysRef = useRef<string[]>([]);

  const onChange = (keys: string | string[]) => {
    const newKeys = Array.isArray(keys) ? keys : [keys];
    closedKeysRef.current = activeKeys.filter((key) => !newKeys.includes(key));
    setActiveKeys(newKeys);
  };

  useEffect(() => {
    const handleTransitionEnd = (e: TransitionEvent) => {
      const el = e.target as HTMLElement;
      if (
        el.classList.contains("ant-collapse-content") &&
        closedKeysRef.current.length
      ) {
        closedKeysRef.current.forEach((key) => onClose?.(key));
        closedKeysRef.current = [];
      }
    };

    document.addEventListener("transitionend", handleTransitionEnd);
    return () => {
      document.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [onClose]);

  return (
    <div className={styles.wrapper}>
      <Collapse
        size="small"
        className={styles.collapseWrapper}
        ghost
        activeKey={activeKeys}
        onChange={onChange}
        expandIconPosition="end"
        expandIcon={() => null}
        items={items.map((item) => {
          const progress = item.header.progress;
          return {
            key: item.key,
            label: (
              <div className={styles.headerWrapper}>
                <div className={styles.headerTop}>
                  <Tooltip title={item.header.tooltip} mouseEnterDelay={0.4}>
                    <span className={styles.itemLabel}>
                      {item.header.label}
                      {item.header.tooltip && (
                        <InfoCircleOutlined className={styles.infoIcon} />
                      )}
                    </span>
                  </Tooltip>

                  <div className={styles.iconWrapper}>
                    {item.textButton && (
                      <span className={styles.progressPercent}>
                        {item.textButton}
                      </span>
                    )}
                    {!item.noCollapse && (
                      <ChevronIcon
                        direction={
                          activeKeys.includes(item.key) ? "up" : "down"
                        }
                      />
                    )}
                  </div>
                </div>
                {progress && (
                  <VLProgressBar
                    maxItems={progress.maxItems}
                    itemLength={progress.itemLength}
                  />
                )}
              </div>
            ),
            children: (
              <div className={styles.collapseItem}>{item.children}</div>
            ),
            collapsible:
              !item.children || item.noCollapse ? "disabled" : undefined,
          };
        })}
      />
    </div>
  );
}
